import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, Chip, CircularProgress,
  Drawer, Select, MenuItem, FormControl, InputLabel,
  Skeleton, Divider, Tooltip, Button, TextField, Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  FileText, Calendar, Users, Edit2, Trash2, Upload, Download,
  Send, MessageSquare, Phone, CheckSquare, ArrowRight, Star,
  Bell, Archive, AlertTriangle, Bookmark, Link2, ClipboardList,
  ChevronDown, ChevronUp, Info
} from 'lucide-react';
import apiInstance from 'src/utils/apiAuth';

const CATEGORY_CONFIG = {
  company: { icon: <Edit2 size={14} />, color: '#1976d2', label: 'Company' },
  stage: { icon: <ArrowRight size={14} />, color: '#9c27b0', label: 'Stage' },
  document: { icon: <FileText size={14} />, color: '#2e7d32', label: 'Document' },
  meeting: { icon: <Calendar size={14} />, color: '#ed6c02', label: 'Meeting' },
  note: { icon: <ClipboardList size={14} />, color: '#0288d1', label: 'Note' },
  comment: { icon: <MessageSquare size={14} />, color: '#7b1fa2', label: 'Comment' },
  call_notes: { icon: <Phone size={14} />, color: '#00796b', label: 'Call Notes' },
  evaluation: { icon: <Star size={14} />, color: '#f9a825', label: 'Evaluation' },
  email: { icon: <Send size={14} />, color: '#c62828', label: 'Email' },
  linkedin: { icon: <Link2 size={14} />, color: '#0077b5', label: 'LinkedIn' },
  task: { icon: <CheckSquare size={14} />, color: '#6a1b9a', label: 'Task' },
  member: { icon: <Users size={14} />, color: '#00838f', label: 'Member' },
  person: { icon: <Users size={14} />, color: '#00838f', label: 'Person' },
  outreach: { icon: <Send size={14} />, color: '#2e7d32', label: 'Outreach' },
  invalid: { icon: <AlertTriangle size={14} />, color: '#e65100', label: 'Invalid' },
  newsletter: { icon: <Bookmark size={14} />, color: '#1565c0', label: 'Newsletter' },
  archive: { icon: <Archive size={14} />, color: '#4a148c', label: 'Archive' },
  reminder: { icon: <Bell size={14} />, color: '#ff6f00', label: 'Reminder' },
  calling_card: { icon: <Phone size={14} />, color: '#4caf50', label: 'Calling Card' },
  score_card: { icon: <Star size={14} />, color: '#ff9800', label: 'Score Card' },
  deal_details: { icon: <ClipboardList size={14} />, color: '#3f51b5', label: 'Deal Details' },
  learning: { icon: <FileText size={14} />, color: '#009688', label: 'Learning' },
  sharing: { icon: <Upload size={14} />, color: '#795548', label: 'Sharing' },
  pitch: { icon: <Edit2 size={14} />, color: '#607d8b', label: 'Pitch' },
  stage_assignment: { icon: <Users size={14} />, color: '#455a64', label: 'Assignment' },
};

const ACTION_TYPE_ICONS = {
  created: '✨',
  updated: '✏️',
  deleted: '🗑️',
  uploaded: '📤',
  downloaded: '📥',
  scheduled: '📅',
  cancelled: '❌',
  completed: '✅',
  marked: '🏷️',
  unmarked: '🔓',
  assigned: '👤',
  removed: '➖',
  sent: '📧',
  moved: '↔️',
  renamed: '📝',
  shared: '🔗',
  generated: '⚡',
};

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const groupByDate = (logs) => {
  const groups = {};
  logs.forEach(log => {
    const date = new Date(log.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let key;
    if (date.toDateString() === today.toDateString()) {
      key = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = 'Yesterday';
    } else {
      key = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    if (!groups[key]) groups[key] = [];
    groups[key].push(log);
  });
  return groups;
};

const ActivityTracker = ({ open, onClose, companyId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = useCallback(async (pageNum = 1, append = false) => {
    if (!companyId || !open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum,
        page_size: 30,
      });
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await apiInstance.get(
        `company/${companyId}/activity-log/?${params.toString()}`
      );
      const data = response.data;
      if (data.success) {
        setLogs(prev => append ? [...prev, ...data.data] : data.data);
        setTotal(data.total);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, open, categoryFilter]);

  useEffect(() => {
    if (open && companyId) {
      setLogs([]);
      setPage(1);
      fetchLogs(1, false);
    }
  }, [open, companyId, categoryFilter]);

  const handleLoadMore = () => {
    fetchLogs(page + 1, true);
  };

  const grouped = groupByDate(logs);
  const hasMore = logs.length < total;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100vw',
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 2,
        borderBottom: '1px solid #e2e8f0',
        bgcolor: '#f8fafc'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
            Activity Trail
          </Typography>
          <Chip
            label={total}
            size="small"
            sx={{
              height: 22, fontSize: '0.7rem', fontWeight: 700,
              bgcolor: '#e2e8f0', color: '#475569'
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Filter">
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon fontSize="small" sx={{ color: showFilters ? '#1976d2' : '#64748b' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => fetchLogs(1, false)}>
              <RefreshIcon fontSize="small" sx={{ color: '#64748b' }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafafa' }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ fontSize: '0.85rem' }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>
                      {cfg.icon}
                    </Box>
                    {cfg.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Timeline Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="60%" height={16} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', py: 6, color: '#94a3b8'
          }}>
            <ClipboardList size={48} strokeWidth={1} />
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
              No activity recorded yet
            </Typography>
          </Box>
        ) : (
          Object.entries(grouped).map(([dateLabel, dateLogs]) => (
            <Box key={dateLabel} sx={{ mb: 3 }}>
              {/* Date Header */}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.7rem',
                  mb: 1.5,
                  pl: 0.5
                }}
              >
                {dateLabel}
              </Typography>

              {/* Timeline Items */}
              {dateLogs.map((log, idx) => {
                const catCfg = CATEGORY_CONFIG[log.action_category] || CATEGORY_CONFIG.company;
                const actionEmoji = ACTION_TYPE_ICONS[log.action_type] || '📌';
                const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;

                return (
                  <Box
                    key={log.id}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      mb: 1.5,
                      position: 'relative',
                      '&:hover': { '& .activity-card': { borderColor: catCfg.color + '40' } }
                    }}
                  >
                    {/* Timeline Line */}
                    {idx < dateLogs.length - 1 && (
                      <Box sx={{
                        position: 'absolute',
                        left: 15,
                        top: 34,
                        bottom: -8,
                        width: 2,
                        bgcolor: '#e2e8f0',
                        borderRadius: 1
                      }} />
                    )}

                    {/* Icon */}
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: catCfg.color + '14',
                      color: catCfg.color,
                      flexShrink: 0,
                      border: `1.5px solid ${catCfg.color}30`,
                      zIndex: 1
                    }}>
                      {catCfg.icon}
                    </Box>

                    {/* Content Card */}
                    <Box
                      className="activity-card"
                      sx={{
                        flex: 1,
                        border: '1px solid #f1f5f9',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#fff',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: '#fafbfc',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                        }
                      }}
                    >
                      {/* Top: category chip + time */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Chip
                          label={catCfg.label}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.6rem', fontWeight: 700,
                            bgcolor: catCfg.color + '14', color: catCfg.color,
                            '& .MuiChip-label': { px: 0.8 }
                          }}
                        />
                        <Tooltip title={formatDateTime(log.created_at)}>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', cursor: 'default' }}>
                            {formatRelativeTime(log.created_at)}
                          </Typography>
                        </Tooltip>
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" sx={{
                        fontSize: '0.82rem', color: '#334155',
                        lineHeight: 1.5, fontWeight: 500
                      }}>
                        <span style={{ marginRight: 4 }}>{actionEmoji}</span>
                        {log.description}
                      </Typography>

                      {/* User */}
                      {log.user?.name && (
                        <Typography variant="caption" sx={{
                          color: '#94a3b8', fontSize: '0.7rem', mt: 0.5, display: 'block'
                        }}>
                          by {log.user.name}
                        </Typography>
                      )}

                      {/* Metadata detail chips for key changes */}
                      {hasMetadata && log.metadata.old_values && log.metadata.new_values && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {Object.keys(log.metadata.old_values).filter(k => 
                            log.metadata.old_values[k] !== log.metadata.new_values?.[k]
                          ).slice(0, 4).map(field => (
                            <Box key={field} sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              fontSize: '0.68rem', color: '#64748b', flexWrap: 'wrap'
                            }}>
                              <Chip label={field} size="small" sx={{
                                height: 16, fontSize: '0.6rem', fontWeight: 600,
                                bgcolor: '#f1f5f9', color: '#475569',
                                '& .MuiChip-label': { px: 0.5 }
                              }} />
                              <Typography component="span" sx={{ fontSize: '0.68rem', color: '#ef4444', textDecoration: 'line-through' }}>
                                {String(log.metadata.old_values[field] || '—').substring(0, 40)}
                              </Typography>
                              <Typography component="span" sx={{ fontSize: '0.68rem', color: '#64748b' }}>→</Typography>
                              <Typography component="span" sx={{ fontSize: '0.68rem', color: '#22c55e', fontWeight: 600 }}>
                                {String(log.metadata.new_values?.[field] || '—').substring(0, 40)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Metadata chips for key context items */}
                      {hasMetadata && !log.metadata.old_values && (
                        <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {Object.entries(log.metadata)
                            .filter(([k, v]) => v && !['updated_fields', 'data'].includes(k) && typeof v !== 'object')
                            .slice(0, 4)
                            .map(([k, v]) => (
                              <Chip
                                key={k}
                                label={`${k.replace(/_/g, ' ')}: ${String(v).substring(0, 30)}`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.6rem',
                                  bgcolor: '#f8fafc', color: '#64748b',
                                  border: '1px solid #e2e8f0',
                                  '& .MuiChip-label': { px: 0.6 }
                                }}
                              />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}

        {/* Load More */}
        {hasMore && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleLoadMore}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
              }}
            >
              Load more ({total - logs.length} remaining)
            </Button>
          </Box>
        )}

        {loading && logs.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ActivityTracker;
